import type { Dispatch, FC, SetStateAction } from 'react';
import { useEffect, useMemo } from 'react';

import { useExternalLinkHandlers } from '@confluence/adf-renderer/entry-points/useExternalLinkHandlers.ts';

import type { NBMRendererContentProps } from '../NBMRenderer.tsx';
import { useLegacyContentMacroRendererAnalytics } from '../useLegacyContentMacroRendererAnalytics.ts';

/**
 * Only the parts of the render configuration that genuinely become available after hydration.
 *
 * `dataProviders` is deliberately absent: the bridge owns the provider factory and supplies it from
 * its first render, because a factory attached after mount can never reach the nested extensions
 * (see the note on `NBMRendererHydrationBridge`). `contentId` is absent for a simpler reason — it is
 * the bridge's own prop, so echoing it back could only ever return the same value.
 */
export type NBMRendererHydrationClientConfig = Pick<
	NBMRendererContentProps,
	'allowAnnotations' | 'annotationProvider' | 'eventHandlers' | 'onSetLinkTarget'
>;

export interface NBMRendererHydrationClientEnhancerProps {
	adfDocument: NBMRendererContentProps['adfDocument'];
	annotationProvider?: NBMRendererContentProps['annotationProvider'];
	contentId?: string;
	onLoad: Dispatch<SetStateAction<NBMRendererHydrationClientConfig | undefined>>;
}

export const NBMRendererHydrationClientEnhancer: FC<NBMRendererHydrationClientEnhancerProps> = ({
	adfDocument,
	annotationProvider,
	contentId,
	onLoad,
}) => {
	const { eventHandlers, onSetLinkTarget } = useExternalLinkHandlers();
	const clientConfig = useMemo<NBMRendererHydrationClientConfig>(
		() => ({
			allowAnnotations: true,
			annotationProvider,
			eventHandlers,
			onSetLinkTarget,
		}),
		[annotationProvider, eventHandlers, onSetLinkTarget],
	);

	useEffect(() => {
		onLoad(clientConfig);
	}, [clientConfig, onLoad]);

	useLegacyContentMacroRendererAnalytics({ adfDocument, contentId });

	return null;
};
